import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Spin } from "antd";
import * as Sentry from "@sentry/react";

import { useProductStore } from "@/_zustand/store";
import { useMachine } from "@/_zustand/machine";
import { useAuth } from "@/_zustand/auth";
import { useOrder } from "@/_zustand/order";
import {
  useCreateOrder,
  useReverseOneClickTransaction,
} from "@/hooks/useTransbank";
import { mapCartForOrder, buildAppliedDiscount } from "@/lib";
import { useOneClickComercio } from "@/hooks/useOneClickComercio";
import {
  adquirirCandadoPago,
  liberarCandadoPago,
  esTokenVigente,
  reclamarIntento,
  PAYMENT_MSG_EN_CURSO,
} from "@/lib/paymentGuard";
import { filtrarCredencialesParaBody } from "@/lib/transbankCredenciales";

export const PayWithDiscount = () => {
  const navigate = useNavigate();
  const { user, incrementTotalPedidos } = useAuth();
  const { setOrder } = useOrder();
  const reverseTransaction = useReverseOneClickTransaction();
  const { credenciales } = useOneClickComercio();
  const {
    calculateTotals,
    total,
    products,
    buyOrder,
    setFinishOrder,
    setPayStatus,
    descuento_id,
    descuento_data,
  } = useProductStore();
  const { machineSelected } = useMachine();
  //const sendOrder = useSendOrder();
  const createOrder = useCreateOrder();
  const [loading, setLoading] = useState(false);
  const reversaLanzada = useRef(false);
  // Token de ownership del candado (ver paymentGuard.js): compartido entre
  // el efecto de montaje y sendPedido/reverseOneClickTransaction, funciones
  // separadas del componente que operan sobre el mismo intento.
  const candadoTokenRef = useRef(null);
  // Credenciales del intento, capturadas al montar (mismo patrón que
  // CardOneClick.jsx/pay-detail OneClick.jsx) para que la reversa no dependa
  // de `machine` "fresco" si cambia entre el montaje y la reversa.
  const credencialesIntentoRef = useRef(credenciales);

  useEffect(() => {
    calculateTotals(machineSelected);
    credencialesIntentoRef.current = credenciales;

    // Candado (comparte flujo con OneClick: un pago OneClick en curso en
    // esta pestaña bloquea "pagar con descuento" y viceversa) + claim
    // persistido por buyOrder (AC 18, edge 7: F5 con el mismo buyOrder no
    // vuelve a crear el pedido).
    const candadoAdquirido = adquirirCandadoPago("paywithdiscount");
    if (!candadoAdquirido || !reclamarIntento("pwd:" + buyOrder)) {
      // Sólo liberar el candado si este montaje fue quien lo adquirió: si
      // adquirirCandadoPago devolvió false, el candado pertenece a otro
      // intento genuinamente en vuelo y no debe tocarse.
      if (candadoAdquirido) {
        liberarCandadoPago("paywithdiscount", candadoAdquirido);
      }
      toast.error(PAYMENT_MSG_EN_CURSO);
      navigate("/cart", { replace: true });
      return;
    }
    candadoTokenRef.current = candadoAdquirido;

    sendPedido();
    // Efecto de montaje único: solo debe correr una vez al entrar a esta
    // pantalla para disparar el pedido; machineSelected/calculateTotals
    // pueden cambiar de identidad sin que corresponda re-disparar el pedido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendPedido = () => {
    let dataSend = {
      appPlatform: "web",
      userId: user.usuario_id,
      cart: mapCartForOrder(products, machineSelected),
      storeId: machineSelected,
      total: 0,
      buyOrder: buyOrder,
      appliedDiscount: buildAppliedDiscount(descuento_data, descuento_id),

      transactionDetail: null,
    };

    createOrder
      .mutateAsync(dataSend)
      .then((resp) => {
        //MANEJAR ERROR
        if (resp.status === "success") {
          // Segunda capa de ownership (ver paymentGuard.js): esta respuesta
          // puede ser la llegada tardía de un intento cuyo candado ya
          // expiró por TTL y fue re-adquirido por un reintento más nuevo
          // (remount). Si ya no es el dueño vigente, se descarta
          // silenciosamente esta rama en vez de seguir con los efectos de
          // éxito (evita un doble pedido).
          if (!esTokenVigente("paywithdiscount", candadoTokenRef.current)) {
            Sentry.captureMessage(
              "Respuesta tardía de un intento de pago obsoleto descartada",
              {
                level: "warning",
                tags: { payment_flow: "paywithdiscount" },
                extra: {
                  buyOrder,
                  maquina_id: machineSelected,
                  motivo: "token_candado_ya_no_vigente",
                },
              },
            );
            return;
          }
          // Camino de éxito (AC 6): el candado NO se libera.
          setOrder({ ...resp, pedido_total: 0 });
          setFinishOrder(machineSelected);
          toast.success("EXITO");
          incrementTotalPedidos();
          return navigate(`/order`, { replace: true });
        } else {
          liberarCandadoPago("paywithdiscount", candadoTokenRef.current);
          toast.error(resp.message);
          //return navigate(`/cart`, {replace: true});
        }
      })
      .catch(() => {
        // Misma segunda capa de ownership que la rama de éxito (ver
        // paymentGuard.js): un error tardío de un intento obsoleto (candado
        // ya re-adquirido por un intento más nuevo) no debe reversar una
        // transacción que no le corresponde a este intento.
        if (!esTokenVigente("paywithdiscount", candadoTokenRef.current)) {
          Sentry.captureMessage(
            "Respuesta tardía de un intento de pago obsoleto descartada",
            {
              level: "warning",
              tags: { payment_flow: "paywithdiscount" },
              extra: {
                buyOrder,
                maquina_id: machineSelected,
                motivo: "token_candado_ya_no_vigente",
              },
            },
          );
          return;
        }
        toast.error("Error al realizar el pedido");
        reverseOneClickTransaction();
        //MANEJAR ERROR
      });
  };

  const reverseOneClickTransaction = () => {
    // AC 10 (equivalente): la reversa se ejecuta una sola vez.
    if (reversaLanzada.current) return;
    reversaLanzada.current = true;

    setPayStatus(false);

    const cred = credencialesIntentoRef.current ?? credenciales;

    let formData = {
      buyOrder: buyOrder,
      codigoTienda: cred.codeStore,
      total: total,
      maquina_id: machineSelected,
      ...filtrarCredencialesParaBody({
        codeComerce: cred.codeComerce,
        codeStore: cred.codeStore,
        apiKey: cred.apiKey,
      }),
    };
    reverseTransaction
      .mutateAsync(formData)
      .then(() => {
        liberarCandadoPago("paywithdiscount", candadoTokenRef.current);
        return navigate("/cart", { replace: true });
      })
      .catch((error) => {
        // Riesgo financiero: el cobro pudo haber quedado hecho y la reversa
        // (el intento de anularlo) falló — necesita revisión manual.
        Sentry.captureException(error, {
          level: "error",
          tags: { financial_risk: true, payment_flow: "oneclick" },
          extra: { buyOrder, maquina_id: machineSelected },
        });
        liberarCandadoPago("paywithdiscount", candadoTokenRef.current);
        setLoading(false);
        toast.error("Error al reversar la transacción");
        return navigate("/cart", { replace: true });
      });
  };

  if (loading) {
    return (
      <div className="row">
        <div className="col-12 mb-3 inter-regular text-center">
          <h3 className="text-black inter-semibold ">
            Validando la compra, por favor espere!
          </h3>
          <Spin tip="Verificando..." size="large">
            <div
              style={{
                padding: 50,
                borderRadius: 4,
              }}
            />
          </Spin>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12 mb-3 inter-regular text-center">
        <h3 className="text-black inter-semibold ">
          Espere un momento, estamos validando su compra!
        </h3>
        <div className="mt-4"></div>
      </div>
    </div>
  );
};
